import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useShift } from '../../../context/ShiftContext';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { SHIFT_TYPES, SHIFT_STATUS } from '../../../data/nurseAppModels';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import SwapShiftModal from './SwapShiftModal';

const ShiftListPage = () => {
  const navigate = useNavigate();
  const { shifts, getUpcomingShifts, getTodayShifts, clockIn, clockOut } = useShift();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('today');
  const [viewMode, setViewMode] = useState('planner'); // 'planner' or 'calendar'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]); // Array of user IDs to filter
  const [teamMembersWithProfile, setTeamMembersWithProfile] = useState([]);
  const [showMemberFilter, setShowMemberFilter] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [swapModalOpen, setSwapModalOpen] = useState({ isOpen: false, shiftId: null, shiftData: null });

  // Fetch team members with profile data
  useEffect(() => {
    const fetchTeamMembersWithProfile = async () => {
      if (!currentTeam || !currentTeam.members) return;

      try {
        setLoadingMembers(true);
        const membersWithProfile = await Promise.all(
          currentTeam.members.map(async (member) => {
            try {
              const userRef = doc(db, 'users', member.uid);
              const userSnap = await getDoc(userRef);
              const userData = userSnap.exists() ? userSnap.data() : null;
              
              return {
                ...member,
                photoURL: userData?.photoURL || null,
                displayName: userData?.displayName || member.name || 'Unknown'
              };
            } catch (err) {
              console.error(`Error fetching user ${member.uid}:`, err);
              return {
                ...member,
                photoURL: null,
                displayName: member.name || 'Unknown'
              };
            }
          })
        );
        setTeamMembersWithProfile(membersWithProfile);
        
        // Initialize selectedMembers with all members
        if (selectedMembers.length === 0) {
          setSelectedMembers(membersWithProfile.map(m => m.uid));
        }
      } catch (err) {
        console.error('Error fetching team members:', err);
      } finally {
        setLoadingMembers(false);
      }
    };

    if (currentTeam) {
      fetchTeamMembersWithProfile();
    }
  }, [currentTeam]);

  // Get shifts for selected date (with member filter)
  const getShiftsForDate = (date) => {
    if (!shifts || shifts.length === 0) return [];
    const dateStr = date.toISOString().split('T')[0];
    return shifts.filter(shift => {
      // Filter by selected members
      if (selectedMembers.length > 0 && !selectedMembers.includes(shift.assignedTo)) {
        return false;
      }
      
      const shiftDate = shift.startTime?.toDate ? shift.startTime.toDate() : 
                         shift.startTime?.seconds ? new Date(shift.startTime.seconds * 1000) : null;
      if (!shiftDate) return false;
      const shiftDateStr = shiftDate.toISOString().split('T')[0];
      return shiftDateStr === dateStr;
    });
  };

  // Get shifts for selected date sorted by time (with member filter)
  const selectedDateShifts = useMemo(() => {
    if (!shifts || shifts.length === 0) return [];
    const dateStr = selectedDate.toISOString().split('T')[0];
    const filteredShifts = shifts.filter(shift => {
      // Filter by selected members
      if (selectedMembers.length > 0 && !selectedMembers.includes(shift.assignedTo)) {
        return false;
      }
      
      const shiftDate = shift.startTime?.toDate ? shift.startTime.toDate() : 
                       shift.startTime?.seconds ? new Date(shift.startTime.seconds * 1000) : null;
      if (!shiftDate) return false;
      const shiftDateStr = shiftDate.toISOString().split('T')[0];
      return shiftDateStr === dateStr;
    });
    
    return filteredShifts.sort((a, b) => {
      const timeA = a.startTime?.toDate ? a.startTime.toDate() : 
                    a.startTime?.seconds ? new Date(a.startTime.seconds * 1000) : new Date();
      const timeB = b.startTime?.toDate ? b.startTime.toDate() : 
                    b.startTime?.seconds ? new Date(b.startTime.seconds * 1000) : new Date();
      return timeA - timeB;
    });
  }, [selectedDate, shifts, selectedMembers]);

  // Filter shifts by selected members for tabs
  const filteredShifts = useMemo(() => {
    if (!shifts || shifts.length === 0) return [];
    if (selectedMembers.length === 0) return shifts;
    return shifts.filter(shift => selectedMembers.includes(shift.assignedTo));
  }, [shifts, selectedMembers]);

  const upcomingShifts = useMemo(() => {
    if (!shifts || shifts.length === 0) return [];
    const allUpcoming = getUpcomingShifts();
    if (selectedMembers.length === 0) return allUpcoming;
    return allUpcoming.filter(shift => selectedMembers.includes(shift.assignedTo));
  }, [shifts, selectedMembers, getUpcomingShifts]);

  const todayShifts = useMemo(() => {
    if (!shifts || shifts.length === 0) return [];
    const allToday = getTodayShifts();
    if (selectedMembers.length === 0) return allToday;
    return allToday.filter(shift => selectedMembers.includes(shift.assignedTo));
  }, [shifts, selectedMembers, getTodayShifts]);

  const myShifts = useMemo(() => {
    if (!filteredShifts || filteredShifts.length === 0) return [];
    return filteredShifts.filter(s => s.assignedTo === currentUser?.uid);
  }, [filteredShifts, currentUser]);

  if (!currentTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">กรุณาเลือกทีมก่อน</p>
          <Link
            to="/nurse/teams"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            ไปเลือกทีม
          </Link>
        </div>
      </div>
    );
  }

  // Generate 5 days starting from selected date
  const getDateRange = () => {
    const dates = [];
    const startDate = new Date(selectedDate);
    startDate.setDate(startDate.getDate() - 2); // Start 2 days before
    
    for (let i = 0; i < 5; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const dateRange = getDateRange();

  // Get month name in Thai
  const getMonthName = (date) => {
    const months = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];
    return months[date.getMonth()];
  };

  // Get day name in Thai
  const getDayName = (date) => {
    const days = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];
    return days[date.getDay()];
  };

  // Navigate months
  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  // Get calendar days for current month
  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Previous month days
    const prevMonth = new Date(year, month - 1, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthDays - i),
        isCurrentMonth: false
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }

    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }

    return days;
  };

  const handleClockIn = async (shiftId) => {
    setLoading(true);
    const result = await clockIn(shiftId);
    if (!result.success) {
      alert('เกิดข้อผิดพลาด: ' + result.error);
    }
    setLoading(false);
  };

  const handleClockOut = async (shiftId) => {
    setLoading(true);
    const result = await clockOut(shiftId);
    if (!result.success) {
      alert('เกิดข้อผิดพลาด: ' + result.error);
    }
    setLoading(false);
  };

  const getShiftTypeLabel = (type) => {
    switch (type) {
      case SHIFT_TYPES.MORNING: return 'เช้า';
      case SHIFT_TYPES.AFTERNOON: return 'บ่าย';
      case SHIFT_TYPES.NIGHT: return 'ดึก';
      default: return type;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case SHIFT_STATUS.SCHEDULED: return 'กำหนดแล้ว';
      case SHIFT_STATUS.CONFIRMED: return 'ยืนยันแล้ว';
      case SHIFT_STATUS.IN_PROGRESS: return 'กำลังทำงาน';
      case SHIFT_STATUS.COMPLETED: return 'เสร็จสิ้น';
      case SHIFT_STATUS.CANCELLED: return 'ยกเลิก';
      default: return status;
    }
  };

  const getShiftTypeColor = (type) => {
    switch (type) {
      case SHIFT_TYPES.MORNING: return 'bg-green-100 text-green-700 border-green-300';
      case SHIFT_TYPES.AFTERNOON: return 'bg-orange-100 text-orange-700 border-orange-300';
      case SHIFT_TYPES.NIGHT: return 'bg-blue-100 text-blue-700 border-blue-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  // Get shift type icon
  const getShiftTypeIcon = (type) => {
    switch (type) {
      case SHIFT_TYPES.MORNING: return '🌅';
      case SHIFT_TYPES.AFTERNOON: return '☀️';
      case SHIFT_TYPES.NIGHT: return '🌙';
      default: return '📅';
    }
  };

  // Get user profile image
  const getUserProfileImage = (userId) => {
    const member = teamMembersWithProfile.find(m => m.uid === userId);
    return member?.photoURL || null;
  };

  // Get user display name
  const getUserDisplayName = (userId) => {
    const member = teamMembersWithProfile.find(m => m.uid === userId);
    return member?.displayName || 'Unknown';
  };

  // Toggle member selection
  const toggleMemberSelection = (memberId) => {
    setSelectedMembers(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  // Select all members
  const selectAllMembers = () => {
    setSelectedMembers(teamMembersWithProfile.map(m => m.uid));
  };

  // Deselect all members
  const deselectAllMembers = () => {
    setSelectedMembers([]);
  };

  // Generate time slots for timeline
  const timeSlots = [];
  for (let hour = 0; hour < 24; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
    if (hour < 23) {
      timeSlots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
  }

  // Check if date has shifts (with member filter)
  const dateHasShifts = (date) => {
    return getShiftsForDate(date).length > 0;
  };

  // Get shifts for a specific date (for calendar display)
  const getShiftsForDateDisplay = (date) => {
    return getShiftsForDate(date);
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Check if date is selected
  const isSelected = (date) => {
    return date.toDateString() === selectedDate.toDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-1">
                {viewMode === 'planner' ? 'Planner' : 'Calendar'}
              </h1>
              <p className="text-sm text-gray-600">
                Plan time on your calendar to achieve your results.
              </p>
            </div>
            <div className="flex gap-2">
              {/* Swap History Button */}
              <Link
                to="/nurse/shifts/swaps/history"
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="hidden sm:inline">ประวัติการแลกเวร</span>
              </Link>
              {/* Member Filter Button */}
              <div className="relative">
                <button
                  onClick={() => setShowMemberFilter(!showMemberFilter)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="hidden sm:inline">กรองสมาชิก</span>
                  {selectedMembers.length < teamMembersWithProfile.length && (
                    <span className="ml-1 px-2 py-0.5 text-xs bg-orange-500 text-white rounded-full">
                      {selectedMembers.length}
                    </span>
                  )}
                </button>

                {/* Member Filter Dropdown */}
                {showMemberFilter && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
                    <div className="p-4 border-b border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-gray-900">กรองตามสมาชิก</h3>
                        <button
                          onClick={() => setShowMemberFilter(false)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={selectAllMembers}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition"
                        >
                          เลือกทั้งหมด
                        </button>
                        <button
                          onClick={deselectAllMembers}
                          className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition"
                        >
                          ยกเลิกทั้งหมด
                        </button>
                      </div>
                    </div>
                    <div className="p-2">
                      {teamMembersWithProfile.map((member) => {
                        const isSelected = selectedMembers.includes(member.uid);
                        return (
                          <button
                            key={member.uid}
                            onClick={() => toggleMemberSelection(member.uid)}
                            className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition"
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              isSelected ? 'ring-2 ring-orange-500' : ''
                            }`}>
                              {member.photoURL ? (
                                <img
                                  src={member.photoURL}
                                  alt={member.displayName}
                                  className="w-full h-full rounded-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div
                                className={`w-full h-full rounded-full bg-gray-300 flex items-center justify-center text-white text-xs font-semibold ${
                                  member.photoURL ? 'hidden' : 'flex'
                                }`}
                              >
                                {member.displayName?.charAt(0) || 'U'}
                              </div>
                            </div>
                            <span className="flex-1 text-left text-sm text-gray-700">
                              {member.displayName}
                            </span>
                            {isSelected && (
                              <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={() => setViewMode(viewMode === 'planner' ? 'calendar' : 'planner')}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition flex items-center gap-2"
              >
                <span>{viewMode === 'planner' ? '📅' : '📋'}</span>
                <span className="hidden sm:inline">{viewMode === 'planner' ? 'Calendar' : 'Planner'}</span>
              </button>
              <Link
                to="/nurse/shifts/create"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
              >
                <span>+</span>
                <span className="hidden sm:inline">สร้างเวร</span>
              </Link>
            </div>
          </div>
        </div>

        {viewMode === 'planner' ? (
          <>
            {/* Month Navigation */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    const newDate = new Date(selectedDate);
                    newDate.setMonth(newDate.getMonth() - 1);
                    setSelectedDate(newDate);
                    setCurrentMonth(newDate);
                  }}
                  className="px-3 py-1 text-gray-600 hover:text-gray-900"
                >
                  ← {getMonthName(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1))}
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-gray-900">
                    {getMonthName(selectedDate)}
                  </span>
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                <button
                  onClick={() => {
                    const newDate = new Date(selectedDate);
                    newDate.setMonth(newDate.getMonth() + 1);
                    setSelectedDate(newDate);
                    setCurrentMonth(newDate);
                  }}
                  className="px-3 py-1 text-gray-600 hover:text-gray-900"
                >
                  {getMonthName(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1))} →
                </button>
              </div>
            </div>

            {/* Daily Date Selection */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
              <div className="flex gap-2 overflow-x-auto">
                {dateRange.map((date, index) => {
                  const hasShifts = dateHasShifts(date);
                  const isSelectedDate = isSelected(date);
                  const isTodayDate = isToday(date);

                  return (
                    <button
                      key={index}
                      onClick={() => setSelectedDate(date)}
                      className={`flex-shrink-0 px-4 py-3 rounded-lg transition ${
                        isSelectedDate
                          ? 'bg-orange-500 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-sm font-medium">
                          {date.getDate()}
                        </div>
                        <div className="text-xs mt-1">
                          {getDayName(date)}
                        </div>
                        {hasShifts && (
                          <div className="mt-1 flex items-center justify-center gap-0.5 flex-wrap">
                            {getShiftsForDateDisplay(date).slice(0, 3).map((shift, shiftIndex) => {
                              const profileImage = getUserProfileImage(shift.assignedTo);
                              return (
                                <div key={shiftIndex} className="relative">
                                  {profileImage ? (
                                    <img
                                      src={profileImage}
                                      alt={getUserDisplayName(shift.assignedTo)}
                                      className={`w-4 h-4 rounded-full border ${
                                        isSelectedDate ? 'border-white' : 'border-orange-500'
                                      }`}
                                      title={`${getShiftTypeIcon(shift.shiftType)} ${getUserDisplayName(shift.assignedTo)}`}
                                    />
                                  ) : (
                                    <div
                                      className={`w-4 h-4 rounded-full text-[8px] flex items-center justify-center border ${
                                        isSelectedDate
                                          ? 'bg-white text-orange-500 border-white'
                                          : 'bg-orange-500 text-white border-orange-500'
                                      }`}
                                      title={`${getShiftTypeIcon(shift.shiftType)} ${getUserDisplayName(shift.assignedTo)}`}
                                    >
                                      {getShiftTypeIcon(shift.shiftType)}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {getShiftsForDateDisplay(date).length > 3 && (
                              <span className={`text-[8px] ${
                                isSelectedDate ? 'text-white' : 'text-orange-500'
                              }`}>
                                +{getShiftsForDateDisplay(date).length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Today's Shifts Timeline */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Today</h2>
                  <p className="text-sm text-gray-500">Recently tasks</p>
                </div>
              </div>

              {selectedDateShifts.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">ไม่มีเวรในวันนี้</p>
                </div>
              ) : (
                <div className="relative" style={{ 
                  minHeight: `${Math.max(selectedDateShifts.length * 80, 600)}px`,
                  paddingBottom: '120px'
                }}>
                  {/* Timeline */}
                  <div className="absolute left-0 top-0 bottom-0 w-16 border-r border-gray-200">
                    {timeSlots.filter((_, index) => index % 2 === 0).map((time, index) => (
                      <div
                        key={index}
                        className="h-12 flex items-start justify-end pr-2 text-xs text-gray-500"
                      >
                        {time}
                      </div>
                    ))}
                  </div>

                  {/* Shifts */}
                  <div className="ml-16 pl-4">
                    {selectedDateShifts.map((shift, index) => {
                      const startTime = shift.startTime?.toDate ? shift.startTime.toDate() : 
                                       shift.startTime?.seconds ? new Date(shift.startTime.seconds * 1000) : new Date();
                      const endTime = shift.endTime?.toDate ? shift.endTime.toDate() : 
                                     shift.endTime?.seconds ? new Date(shift.endTime.seconds * 1000) : new Date();
                      
                      const startHour = startTime.getHours();
                      const startMinute = startTime.getMinutes();
                      const endHour = endTime.getHours();
                      const endMinute = endTime.getMinutes();
                      
                      const startPosition = (startHour * 2) + (startMinute >= 30 ? 1 : 0);
                      const duration = ((endHour - startHour) * 2) + ((endMinute - startMinute) >= 30 ? 1 : 0);

                      const isMyShift = shift.assignedTo === currentUser?.uid;
                      const canClockIn = isMyShift && shift.status === SHIFT_STATUS.CONFIRMED;
                      const canClockOut = isMyShift && shift.status === SHIFT_STATUS.IN_PROGRESS;

                      return (
                        <div
                          key={shift.id}
                          className={`mb-2 p-3 rounded-lg border ${
                            shift.shiftType === SHIFT_TYPES.MORNING ? 'bg-green-50 border-green-200' :
                            shift.shiftType === SHIFT_TYPES.AFTERNOON ? 'bg-orange-50 border-orange-200' :
                            'bg-blue-50 border-blue-200'
                          }`}
                          style={{
                            marginTop: `${startPosition * 3}px`,
                            minHeight: `${Math.max(duration * 3, 60)}px`
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-lg">{getShiftTypeIcon(shift.shiftType)}</span>
                                <h3 className="font-bold text-gray-900">
                                  เวร{getShiftTypeLabel(shift.shiftType)}
                                </h3>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">
                                {startTime.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })} - {' '}
                                {endTime.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                              <div className="flex items-center gap-2">
                                {getUserProfileImage(shift.assignedTo) ? (
                                  <img
                                    src={getUserProfileImage(shift.assignedTo)}
                                    alt={getUserDisplayName(shift.assignedTo)}
                                    className="w-6 h-6 rounded-full border border-gray-300"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div
                                  className={`w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-white text-xs font-semibold ${
                                    getUserProfileImage(shift.assignedTo) ? 'hidden' : 'flex'
                                  }`}
                                >
                                  {getUserDisplayName(shift.assignedTo)?.charAt(0) || 'U'}
                                </div>
                                <p className="text-sm text-gray-600">
                                  {getUserDisplayName(shift.assignedTo)}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2 ml-4">
                              {canClockIn && (
                                <button
                                  onClick={() => handleClockIn(shift.id)}
                                  disabled={loading}
                                  className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition disabled:opacity-50"
                                >
                                  เข้าเวร
                                </button>
                              )}
                              {canClockOut && (
                                <button
                                  onClick={() => handleClockOut(shift.id)}
                                  disabled={loading}
                                  className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition disabled:opacity-50"
                                >
                                  ออกเวร
                                </button>
                              )}
                              {isMyShift && shift.status === SHIFT_STATUS.SCHEDULED && (
                                <button
                                  onClick={() => setSwapModalOpen({ isOpen: true, shiftId: shift.id, shiftData: shift })}
                                  className="px-3 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 transition flex items-center gap-1 justify-center"
                                >
                                  <span>🔄</span>
                                  <span>ยื่นขอแลกเวร</span>
                                </button>
                              )}
                              <Link
                                to={`/nurse/shifts/${shift.id}`}
                                className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 transition text-center"
                              >
                                ดู
                              </Link>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Calendar View */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => navigateMonth(-1)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h2 className="text-xl font-semibold text-gray-900">
                  {getMonthName(currentMonth)} {currentMonth.getFullYear() + 543}
                </h2>
                <button
                  onClick={() => navigateMonth(1)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-2">
                {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((day) => (
                  <div key={day} className="text-center text-sm font-medium text-gray-600 py-2">
                    {day}
                  </div>
                ))}
                {getCalendarDays().map((dayObj, index) => {
                  const date = dayObj.date;
                  const hasShifts = dateHasShifts(date);
                  const isTodayDate = isToday(date);
                  const isSelectedDate = isSelected(date);

                  return (
                    <button
                      key={index}
                      onClick={() => {
                        setSelectedDate(date);
                        setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));
                        setViewMode('planner');
                      }}
                      className={`p-2 rounded-lg transition ${
                        !dayObj.isCurrentMonth
                          ? 'text-gray-300'
                          : isSelectedDate
                          ? 'bg-orange-500 text-white'
                          : isTodayDate
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="text-sm font-medium">{date.getDate()}</div>
                      {hasShifts && dayObj.isCurrentMonth && (
                        <div className="mt-1 flex items-center justify-center gap-0.5 flex-wrap">
                          {getShiftsForDateDisplay(date).slice(0, 2).map((shift, shiftIndex) => {
                            const profileImage = getUserProfileImage(shift.assignedTo);
                            return (
                              <div key={shiftIndex} className="relative" title={`${getShiftTypeIcon(shift.shiftType)} ${getUserDisplayName(shift.assignedTo)}`}>
                                {profileImage ? (
                                  <img
                                    src={profileImage}
                                    alt={getUserDisplayName(shift.assignedTo)}
                                    className={`w-3 h-3 rounded-full border ${
                                      isSelectedDate ? 'border-white' : 'border-orange-500'
                                    }`}
                                  />
                                ) : (
                                  <span className={`text-[8px] ${
                                    isSelectedDate ? 'text-white' : 'text-orange-500'
                                  }`}>
                                    {getShiftTypeIcon(shift.shiftType)}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                          {getShiftsForDateDisplay(date).length > 2 && (
                            <span className={`text-[8px] ${
                              isSelectedDate ? 'text-white' : 'text-orange-500'
                            }`}>
                              +{getShiftsForDateDisplay(date).length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Today's Tasks Summary */}
              {selectedDateShifts.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Today tasks</h3>
                  <div className="space-y-2">
                    {selectedDateShifts.slice(0, 2).map((shift) => {
                      const startTime = shift.startTime?.toDate ? shift.startTime.toDate() : 
                                       shift.startTime?.seconds ? new Date(shift.startTime.seconds * 1000) : new Date();
                      
                      return (
                        <div
                          key={shift.id}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                        >
                          <div className={`w-8 h-8 rounded flex items-center justify-center ${
                            shift.shiftType === SHIFT_TYPES.MORNING ? 'bg-green-100' :
                            shift.shiftType === SHIFT_TYPES.AFTERNOON ? 'bg-orange-100' :
                            'bg-blue-100'
                          }`}>
                            <span className="text-xs">
                              {shift.shiftType === SHIFT_TYPES.MORNING ? '↓' :
                               shift.shiftType === SHIFT_TYPES.AFTERNOON ? '→' : '↑'}
                            </span>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              เวร{getShiftTypeLabel(shift.shiftType)}
                            </p>
                            <p className="text-xs text-gray-600">
                              {startTime.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Tabs for filtering */}
        <div className="mt-12 mb-4 flex gap-2 border-b border-gray-200 bg-white py-3">
          <button
            onClick={() => {
              setActiveTab('today');
              setSelectedDate(new Date());
            }}
            className={`px-4 py-2 font-medium ${
              activeTab === 'today'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            วันนี้ ({todayShifts.length})
          </button>
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'upcoming'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            กำลังจะมาถึง ({upcomingShifts.length})
          </button>
          <button
            onClick={() => setActiveTab('my')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'my'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            เวรของฉัน ({myShifts.length})
          </button>
          <Link
            to="/nurse/shifts/plans"
            className={`px-4 py-2 font-medium ${
              activeTab === 'plans'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            แผนเวร
          </Link>
        </div>
      </div>

      {/* Swap Shift Modal */}
      <SwapShiftModal
        isOpen={swapModalOpen.isOpen}
        onClose={(success) => {
          setSwapModalOpen({ isOpen: false, shiftId: null, shiftData: null });
          if (success) {
            // Refresh shifts or show success message
          }
        }}
        shiftId={swapModalOpen.shiftId}
        shiftData={swapModalOpen.shiftData}
      />
    </div>
  );
};

export default ShiftListPage;
